import {
  Container,
  EmptyState,
  Flex,
  Heading,
  IconButton,
  Table,
  Text,
  VStack
} from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute, useNavigate, Link as RouterLink } from "@tanstack/react-router"
import { FiEdit2, FiSearch } from "react-icons/fi"
import { z } from "zod"

import { ProviderGroups as ProviderGroupsApi } from "@/client"
import DeleteProviderGroup from "@/components/ProviderGroups/DeleteProviderGroup"
import AddProviderGroup from "@/components/ProviderGroups/AddProviderGroup"
import PendingProviderGroups from "@/components/ProviderGroups/PendingProviderGroups"
import {
  PaginationItems,
  PaginationNextTrigger,
  PaginationPrevTrigger,
  PaginationRoot,
} from "@/components/ui/pagination.tsx"

const providerGroupsSearchSchema = z.object({
  page: z.number().catch(1),
})

const PER_PAGE = 5


function getProviderGroupsQueryOptions({ page, providerId }: { page: number; providerId: string }) {
  return {
    queryFn: () =>
      ProviderGroupsApi.readProviderGroupsByProviderId({ path: { providerId }, query: { skip: (page - 1) * PER_PAGE, limit: PER_PAGE } }),
    queryKey: ["providerGroups", { page, providerId }],
  }
}

export const Route = createFileRoute("/_layout/provider/providergroups/$providerId")({
  component: ProviderGroups,
  validateSearch: (search) => providerGroupsSearchSchema.parse(search),
})

function ProviderGroupsTable() {
  const params = Route.useParams()
  const providerId = params.providerId
  const { page } = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })


  const { data, isLoading, isPlaceholderData } = useQuery({
    ...getProviderGroupsQueryOptions({ page, providerId }),
    placeholderData: (prevData) => prevData,
  })

  const setPage = (page: number) =>
    navigate({
      search: (prev: { page: number }) => ({ ...prev, page }),
    })

  const providerGroups = data?.data.slice(0, PER_PAGE) ?? []
  const count = data?.count ?? 0

  if (isLoading) {
    return <PendingProviderGroups />
  }

  if (providerGroups.length === 0) {
    return (
      <EmptyState.Root>
        <EmptyState.Content>
          <EmptyState.Indicator>
            <FiSearch />
          </EmptyState.Indicator>
          <VStack textAlign="center">
            <EmptyState.Title>You don't have any provider groups yet</EmptyState.Title>
            <EmptyState.Description>
              Add a new provider group to get started
            </EmptyState.Description>
          </VStack>
        </EmptyState.Content>
      </EmptyState.Root>
    )
  }

  return (
    <>
      <Table.Root size="sm" variant="outline">
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeader>Name</Table.ColumnHeader>
            <Table.ColumnHeader>Description</Table.ColumnHeader>
            <Table.ColumnHeader w="80px" />
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {providerGroups?.map((providerGroup: any) => (
            <Table.Row key={providerGroup.id} opacity={isPlaceholderData ? 0.5 : 1}>
              <Table.Cell fontWeight="medium">
                {providerGroup.name || <span style={{ color: "#A0AEC0" }}>—</span>}
              </Table.Cell>
              <Table.Cell>
                {providerGroup.description ? (
                  <Text fontSize="sm" color="gray.600">{providerGroup.description}</Text>
                ) : (
                  <span style={{ color: "#A0AEC0" }}>—</span>
                )}
              </Table.Cell>
              <Table.Cell>
                <Flex gap={1} align="center">
                  <RouterLink to="/provider/providergroups/edit" search={{ providerGroupId: providerGroup.id }}>
                    <IconButton aria-label="Edit group" size="xs" variant="ghost">
                      <FiEdit2 />
                    </IconButton>
                  </RouterLink>
                  <DeleteProviderGroup id={providerGroup.id} iconOnly />
                </Flex>
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table.Root>
      <Flex justifyContent="flex-end" mt={4}>
        <PaginationRoot
          count={count}
          pageSize={PER_PAGE}
          onPageChange={({ page }) => setPage(page)}
        >
          <Flex>
            <PaginationPrevTrigger />
            <PaginationItems />
            <PaginationNextTrigger />
          </Flex>
        </PaginationRoot>
      </Flex>
    </>
  )
}

function ProviderGroups() {
  return (
    <Container maxW="full">
      <Heading size="lg" pt={12}>
        Provider Groups Management
      </Heading>
      <AddProviderGroup />
      <ProviderGroupsTable />
    </Container>
  )
}